/**
 * AI assistant chat state + drawer host. Scope-agnostic core
 * (`AiAssistantProvider`) + two mount wrappers:
 *
 * - `EventScopedAiAssistant` — inside `EventProvider`/`CapabilitiesProvider`
 *   in EventLayoutShell (event chat, ai:use cap). The drawer renders as a
 *   child of the provider: a sibling of <GlobalSearch/> would sit OUTSIDE
 *   EventProvider where the context can't reach it.
 * - `CompanyScopedAiAssistant` — in DashboardShell (no event open): chats
 *   against the company endpoint using the companyId cookie; the server's
 *   role gate (OWNER/ADMIN/EDITOR) is authoritative.
 *
 * Opens via the `openAiAssistant` window event (dispatched by the
 * Setup-guide panel row / AppBar button — the floating launcher was removed
 * on purpose) or the global Cmd/Ctrl+J shortcut.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useLocale } from "next-intl";
import { useLocation, useParams } from "react-router-dom";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useOnboarding } from "@/providers/OnboardingProvider";
import { getCookie } from "@/lib/auth/cookies";
import { tokenStore } from "@/lib/auth/tokenStore";
import { useAiAvailabilityStore, type BuilderSurface, type WizardSurface } from "@/lib/ai/ai-store";
import { clearVeloHandoff, composeVeloHandoffBrief, readVeloHandoff } from "@/lib/ai/velo-handoff";
import type { ImageAttachment } from "@/lib/ai/attachments";
import { useAiToolBridge, type AiClientConfirmation } from "@/lib/ai/ai-tool-bridge";
import { reconcileAiCredits } from "@/lib/ai/creditLedger";
import { AI_DATA_MUTATED_EVENT } from "@/lib/ai-sync/ai-sync";
import {
  streamAiChat,
  streamAiToolResult,
  fetchAiSuggestions,
  listAiConversations,
  getAiConversation,
  deleteAiConversation,
  listAiSupportRequests,
  type AiChatScope,
  type AiConversationSummary,
  type SupportRequestSummary,
  type AiStreamEvent,
  type AiToolResultBody,
} from "@/lib/api/clients/AiClient";
import { AiAssistantDrawer } from "@/components/AiAssistant/AiAssistantDrawer";
import { AiSupportTools } from "@/components/AiAssistant/AiSupportTools";
import { isCreateEventRoute } from "@/lib/immersiveRoutes";

/**
 * Error codes that survive the throw path with their identity intact.
 *
 * A rejection can reach us two ways: as an SSE `error` frame (which already
 * carries its code) or as a thrown non-OK status, where AiClient puts the
 * server's errorCode on Error.message. This set is what keeps the second path
 * from flattening a MEANINGFUL code into the generic "assistant unavailable"
 * — `aiQuotaExceeded` in particular, whose whole UI (buy credits, continue
 * manually, no retry chip) hangs off the code surviving. Every entry has an
 * `aiAssistant.errors.*` message; anything else still falls back, and each
 * consumer additionally guards with `t.has()`.
 */
const PASSTHROUGH_ERROR_CODES = new Set([
  "insufficientCapability",
  "aiQuotaExceeded",
  "conversationNotFound",
  "aiHistoryUnrecoverable",
  "aiToolLoopLimit",
  "noPendingToolUse",
  // The backend's 400 for a message over 8000 characters. The composer checks
  // first, so this only fires for a send that skipped the composer.
  "messageTooLong",
]);

export type AiToolStatus =
  | "running"
  | "awaiting_confirmation"
  | "done"
  | "error"
  | "declined";

export type AiMessage = {
  id: string;
  role: "user" | "assistant" | "tool" | "preview";
  content: string;
  /** role:"user" only — image attachments sent with the message (base64,
   *  rendered read-only as thumbnails in the bubble). */
  images?: ImageAttachment[];
  /** Still receiving deltas. */
  streaming?: boolean;
  /** i18n key under aiAssistant.errors — set on the failed assistant turn. */
  errorCode?: string;
  /** role:"tool" only — one transcript row per tool call. */
  toolUseId?: string;
  toolName?: string;
  toolStatus?: AiToolStatus;
  /** role:"tool" only (P7) — multi-page generation progress. */
  toolProgress?: { page: number; total: number; title: string };
  /** role:"preview" only (P2b) — a generated-HTML preview card. */
  candidateId?: string;
  previewHtml?: string;
  /** role:"preview" only — candidate kind ("document"/"email"/"website"). */
  previewKind?: string;
  /** role:"preview" only (P3 website) — the candidate was published; lives on
   *  the message (not card-local state) so a drawer close/reopen can't
   *  resurrect the Publish button on a consumed candidate. */
  previewPublished?: boolean;
  /** role:"preview" only (P5) — the candidate was SAVED as a template via
   *  the studio (server consumed it); same on-message rationale. */
  previewSaved?: boolean;
  /** role:"assistant" only — short follow-up next-step suggestions emitted at
   *  the end of a clean text turn; rendered as tappable chips under the LAST
   *  assistant bubble (cleared implicitly once a new turn starts). */
  suggestions?: string[];
  /**
   * role:"assistant" only — Velo credits the user's MESSAGE has cost, as
   * billed by the backend (one debit per model call, so an answer that drove
   * the tool loop costs more than a straight reply).
   *
   * Cumulative across every HTTP request the message took: a turn that pauses
   * for a tool confirmation resumes over a second POST, and the backend
   * continues the count rather than restarting it — otherwise the badge
   * counted DOWN mid-turn while the spend went up.
   */
  creditCost?: number;
  /**
   * role:"assistant" only — this row came back from the server transcript
   * rather than from a live stream.
   *
   * Restored rows each carry their OWN message's final total, so every one of
   * them is displayable; the live gating to the last assistant exists only
   * because an intermediate bubble mid-turn can hold a partial. Without this
   * flag, reopening a conversation to check what it cost showed one price out
   * of however many turns it had.
   */
  restored?: boolean;
  /** This turn has finished, so its price is final and safe to keep showing.
   *  Without it a completed turn's cost vanished the moment the user asked the
   *  next question and only came back after a reload — the same turn quoting
   *  two different things depending on when you looked. */
  settled?: boolean;
};

/**
 * A paused tool call awaiting the user (confirm), the page (client), or the
 * user's approval of a confirm-gated CLIENT tool (clientConfirm — pauses on
 * the same ConfirmationCard as the server confirm lane before the handler
 * runs).
 */
export type AiPendingAction = {
  kind: "confirm" | "client" | "clientConfirm";
  toolUseId: string;
  toolName: string;
  toolInputJson: string;
};

// `open` is NOT part of this state — it lives in useAiAvailabilityStore so
// it survives the dashboard↔event shell swap (this provider remounts there).
type AiAssistantState = {
  conversationId: string | null;
  messages: AiMessage[];
  streaming: boolean;
  pendingAction: AiPendingAction | null;
  /** Bumped whenever a NEW user message starts or the transcript is replaced.
   *  A resume belongs to the epoch its pause happened in — see `turnContinue`. */
  turnEpoch: number;
  /** The epoch of the most recent pause. -1 = none. */
  pausedEpoch: number;
};

type AiAction =
  | { type: "newConversation" }
  | { type: "restore"; messages: AiMessage[]; conversationId: string | null }
  | { type: "dropLastTurn" }
  | { type: "turnStart"; userMessageId: string; assistantMessageId: string; content: string; images?: ImageAttachment[] }
  | { type: "turnContinue"; assistantMessageId: string }
  | { type: "conversation"; conversationId: string }
  | { type: "delta"; text: string }
  | { type: "toolUse"; toolMessageId: string; assistantMessageId: string; toolUseId: string; toolName: string }
  | { type: "toolStatus"; toolUseId: string; status: AiToolStatus }
  | { type: "toolProgress"; toolUseId: string; page: number; total: number; title: string }
  | { type: "pause"; action: AiPendingAction }
  | { type: "pauseResolved" }
  | { type: "turnError"; errorCode: string }
  | { type: "suggestions"; messageId: string; items: string[] }
  | { type: "creditCost"; creditCost: number }
  | { type: "turnEnd"; prune?: boolean };

/** Plan §4.5.2: a hung page handler must never strand the conversation. */
const CLIENT_TOOL_TIMEOUT_MS = 10_000;

/**
 * Per-tool timeout overrides (ms) for client tools that legitimately do slow,
 * SIDE-EFFECTING work — the default budget is for instant UI-state mutations.
 * `import_guests_from_spreadsheet` parses a (potentially large) spreadsheet and
 * submits a background job; a premature `clientToolTimeout` would report failure
 * to the model while the job actually submits, risking a duplicate import.
 */
const LONG_RUNNING_CLIENT_TOOLS: Record<string, number> = {
  import_guests_from_spreadsheet: 120_000,
  // Wizard submits POST real resources (slotting = offering → treatments →
  // lanes sequentially); a premature timeout would report failure while the
  // create actually lands, and the model's retry would duplicate it.
  submit_workflow_wizard: 60_000,
  submit_slotting_wizard: 60_000,
  // Website create compresses + POSTs all pages and holds the save overlay.
  submit_website_wizard: 60_000,
};

const INITIAL_STATE: AiAssistantState = {
  conversationId: null,
  messages: [],
  streaming: false,
  pendingAction: null,
  turnEpoch: 0,
  pausedEpoch: -1,
};

/**
 * In-memory per-scope (event/company) transcript cache. Switching scope keeps
 * the provider mounted and wipes the transcript; this lets us restore the
 * outgoing scope's chat when the user comes back, instead of a blank window.
 * SESSION-ONLY — a full page reload clears it (server-side restore would be a
 * separate feature). Capped (oldest-out) so visiting many events can't grow it
 * without bound. Keyed by scopeKey ("event:<id>" / "company:<id>").
 */
const TRANSCRIPT_CACHE = new Map<
  string,
  { messages: AiMessage[]; conversationId: string | null }
>();
const TRANSCRIPT_CACHE_MAX = 12;

function cacheTranscript(
  key: string,
  messages: AiMessage[],
  conversationId: string | null,
): void {
  if (messages.length === 0) {
    TRANSCRIPT_CACHE.delete(key);
    return;
  }
  // Drop any per-message streaming flag — the stream was aborted on switch, so
  // a restored bubble must not show a live caret.
  const frozen = messages.map((m) => (m.streaming ? { ...m, streaming: false } : m));
  TRANSCRIPT_CACHE.delete(key); // re-insert so it becomes most-recent
  TRANSCRIPT_CACHE.set(key, { messages: frozen, conversationId });
  while (TRANSCRIPT_CACHE.size > TRANSCRIPT_CACHE_MAX) {
    const oldest = TRANSCRIPT_CACHE.keys().next().value;
    if (oldest === undefined) break;
    TRANSCRIPT_CACHE.delete(oldest);
  }
}

/**
 * Like {@link patchLastAssistant}, but stops at a user message.
 *
 * The plain version walks back over ANY role, which is right while a turn is
 * streaming (everything after the user bubble belongs to it) and wrong for the
 * end-of-turn credit carry: a turn that produced nothing at all leaves only an
 * empty bubble, the prune removes it, and the walk then runs past the user
 * message and stamps THIS turn's price onto the PREVIOUS turn's answer — which
 * is still the last assistant, so the badge renders the wrong number.
 * Tool rows are skipped; a user message ends the search.
 */
function patchLastAssistantOfTurn(
  messages: AiMessage[],
  patch: (message: AiMessage) => AiMessage,
): AiMessage[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") return messages;
    if (messages[i].role !== "assistant") continue;
    const next = messages.slice();
    next[i] = patch(messages[i]);
    return next;
  }
  return messages;
}

function patchLastAssistant(
  messages: AiMessage[],
  patch: (message: AiMessage) => AiMessage,
): AiMessage[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== "assistant") continue;
    const next = messages.slice();
    next[i] = patch(messages[i]);
    return next;
  }
  return messages;
}

function reducer(state: AiAssistantState, action: AiAction): AiAssistantState {
  switch (action.type) {
    case "newConversation":
      return {
        ...state,
        conversationId: null,
        messages: [],
        streaming: false,
        pendingAction: null,
        // Redundant with the `turnStart` bump on every path reachable today —
        // a stale resume after New chat lands on an empty transcript, where
        // the carry is a no-op anyway, and any follow-up question bumps again.
        // Kept so a future reset path cannot silently inherit a pause; stated
        // here because no test can distinguish it.
        turnEpoch: state.turnEpoch + 1,
      };
    case "restore":
      // Re-hydrate a previously-visited scope's transcript (in-memory cache,
      // see the scope-switch effect). Transient flags are reset — only the
      // persisted message list + conversationId carry over.
      return {
        ...state,
        conversationId: action.conversationId,
        messages: action.messages,
        streaming: false,
        pendingAction: null,
        // The transcript was replaced wholesale — a pause from before it
        // belongs to a turn that is no longer on screen.
        turnEpoch: state.turnEpoch + 1,
      };
    case "dropLastTurn": {
      // Remove the last user message and everything after it (the failed
      // assistant turn) — retryLast re-appends the pair via turnStart.
      let lastUserIdx = -1;
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i].role === "user") {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx === -1) return state;
      return { ...state, messages: state.messages.slice(0, lastUserIdx) };
    }
    case "turnStart":
      return {
        ...state,
        streaming: true,
        // A new user message is a new turn. A client tool still running from
        // the previous one may resume long after this; its epoch is stale and
        // the reducer will leave this turn's price alone.
        turnEpoch: state.turnEpoch + 1,
        // A fresh user message ABANDONS any paused tool call (the backend
        // strips the dangling tool_use on the next turn) — clear the card.
        pendingAction: null,
        messages: [
          ...state.messages,
          {
            id: action.userMessageId,
            role: "user",
            content: action.content,
            ...(action.images && action.images.length ? { images: action.images } : {}),
          },
          { id: action.assistantMessageId, role: "assistant", content: "", streaming: true },
        ],
      };
    case "turnContinue": {
      // Re-entry after a pause: continue streaming into a NEW assistant
      // bubble (no user bubble — the "message" is the tool outcome).
      //
      // The pause closed the stream, so `turnEnd` already ran and marked the
      // bubble we are leaving as settled. It is NOT settled: `credits_charged`
      // carries the total SO FAR for this message, and the message is still
      // going. Leaving the mark on printed a ladder — one message quoting
      // 3, 6, 9, 12, 15, 18 for a single 18-credit charge, which a reader
      // sums to 63 while the ring drops by 18. Only the last row of a message
      // keeps a price, the same rule the restored transcript follows.
      //
      // Two things this must NOT do, both learned the hard way:
      //
      // MOVE the price, never drop it. The resumed request may end without a
      // credits frame at all — a refused or conflicting resume, a stopped one,
      // metering off — and simply clearing left the message showing NOTHING
      // while the customer had already been charged. A hole reads as "that one
      // was free", which is the same lie as the ladder pointing the other way.
      //
      // Only touch OUR turn. `reenter` is reachable from a client tool's
      // async tail, which is not gated on the pending action: a slow handler
      // can resume after the user pressed New chat, switched event, or asked
      // something else, and the transcript has moved on.
      //
      // Keyed on the EPOCH, not on a message id. The first attempt used the id
      // of the bubble open when the pause arrived — and that never matched,
      // because the backend emits `tool_use` before the pause frame, so the
      // open bubble is the empty after-tool one and `turnEnd`'s prune deletes
      // it before any resume runs. The guard was therefore always false and
      // the carry was dead code in production, with the ladder fully back. An
      // epoch survives the prune because it is not tied to a row at all.
      const lastAssistant = [...state.messages].reverse()
        .find((m) => m.role === "assistant");
      const isOurs = state.pausedEpoch === state.turnEpoch;
      const messages = isOurs
        ? patchLastAssistantOfTurn(state.messages, (m) => ({
            ...m,
            creditCost: undefined,
            settled: false,
          }))
        : state.messages;
      return {
        ...state,
        streaming: true,
        messages: [
          ...messages,
          {
            id: action.assistantMessageId,
            role: "assistant",
            content: "",
            streaming: true,
            // Held, not shown: `showCost` needs `!streaming`. If the resumed
            // request quotes a new total it overwrites this; if it quotes
            // nothing, the message still ends with the number it was charged.
            creditCost: isOurs ? lastAssistant?.creditCost : undefined,
          },
        ],
      };
    }
    case "conversation":
      return { ...state, conversationId: action.conversationId };
    case "delta":
      return {
        ...state,
        messages: patchLastAssistant(state.messages, (m) => ({
          ...m,
          content: m.content + action.text,
        })),
      };
    case "toolUse": {
      // Chronology: text streamed so far stays ABOVE the tool row; any text
      // from the model's next step lands in a fresh bubble below it.
      const finalized = patchLastAssistant(state.messages, (m) => ({
        ...m,
        streaming: false,
      }));
      return {
        ...state,
        messages: [
          ...finalized,
          {
            id: action.toolMessageId,
            role: "tool",
            content: "",
            toolUseId: action.toolUseId,
            toolName: action.toolName,
            toolStatus: "running",
          },
          { id: action.assistantMessageId, role: "assistant", content: "", streaming: true },
        ],
      };
    }
    case "toolStatus":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.role === "tool" && m.toolUseId === action.toolUseId
            ? { ...m, toolStatus: action.status }
            : m,
        ),
      };
    case "toolProgress":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.role === "tool" && m.toolUseId === action.toolUseId
            ? {
                ...m,
                toolProgress: {
                  page: action.page,
                  total: action.total,
                  title: action.title,
                },
              }
            : m,
        ),
      };
    case "pause":
      return {
        ...state,
        pendingAction: action.action,
        // Which turn this pause belongs to. NOT a message id: the backend
        // emits `tool_use` BEFORE the pause frame, so the bubble open at
        // pause time is the empty after-tool one, which turnEnd's prune then
        // deletes — an id captured here would never match anything again.
        pausedEpoch: state.turnEpoch,
        messages: state.messages.map((m) =>
          m.role === "tool" && m.toolUseId === action.action.toolUseId
            ? {
                ...m,
                toolStatus:
                  action.action.kind === "confirm" ? "awaiting_confirmation" : "running",
              }
            : m,
        ),
      };
    case "pauseResolved":
      return { ...state, pendingAction: null };
    case "turnError":
      return {
        ...state,
        // A dead conversationId (e.g. the server never persisted the
        // conversation before a disconnect) would otherwise wedge every
        // subsequent send on conversationNotFound — drop it so the next
        // message transparently starts a fresh server-side conversation.
        conversationId:
          action.errorCode === "conversationNotFound" ? null : state.conversationId,
        messages: patchLastAssistant(state.messages, (m) => ({
          ...m,
          errorCode: action.errorCode,
        })),
      };
    case "suggestions":
      // Attach to the SPECIFIC assistant message the suggestions were fetched
      // for (by id, not "last") — the async fetch may resolve after a later
      // turn started; targeting by id keeps stale chips from landing on the
      // wrong bubble. The panel only renders chips for the LAST message, so a
      // newer turn hides them regardless.
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId && m.role === "assistant"
            ? { ...m, suggestions: action.items }
            : m,
        ),
      };
    case "creditCost":
      // Stamped on the LAST assistant bubble, which is the one the running
      // turn is filling. Frames carry the MESSAGE's cumulative cost — across
      // every request a paused turn takes — so a plain overwrite is correct;
      // accumulating here would double-count every re-entry.
      return {
        ...state,
        messages: patchLastAssistant(state.messages, (m) => ({
          ...m,
          creditCost: action.creditCost,
        })),
      };
    case "turnEnd": {
      let messages = patchLastAssistant(state.messages, (m) => ({
        ...m,
        streaming: false,
      }));
      // Natural end (not user-stop): drop a trailing EMPTY assistant bubble —
      // a pause or a tool-only step leaves one behind, and it would render
      // as a misleading "stopped" marker. User-stop keeps it on purpose.
      if (action.prune) {
        const last = messages[messages.length - 1];
        if (last && last.role === "assistant" && !last.content && !last.errorCode) {
          messages = messages.slice(0, -1);
          // Carry the price forward. The last model call's credits frame lands
          // on whichever bubble is open, and after a tool_use that is this
          // empty one — so a turn that ends on a tool (a confirmation pause,
          // the loop limit) used to have its total deleted along with it.
          if (typeof last.creditCost === "number") {
            messages = patchLastAssistantOfTurn(messages, (m) => ({
              ...m,
              creditCost: last.creditCost,
            }));
          }
        }
      }
      // The turn is over either way (natural end or user-stop), so its last
      // assistant now holds a FINAL total — the same thing a restored row
      // holds. Marking it is what makes the live transcript agree with the
      // reopened one instead of showing a hole where an earlier turn's price
      // used to be.
      messages = patchLastAssistantOfTurn(messages, (m) => ({ ...m, settled: true }));
      return { ...state, streaming: false, messages };
    }
    default:
      return state;
  }
}

export type AiAssistantContextType = {
  open: boolean;
  messages: AiMessage[];
  streaming: boolean;
  available: boolean;
  /** "event" | "company" — lets the drawer pick scope-appropriate copy. */
  scopeKind: AiChatScope["kind"] | null;
  /** Event id when event-scoped, else null. */
  scopeEventId: string | null;
  /** Live conversation id (reads a ref — stable across turns; null before the
   *  first turn). Used by the support tools to attach the chat to an escalation. */
  getConversationId: () => string | null;
  openDrawer: () => void;
  closeDrawer: () => void;
  newConversation: () => void;
  /** Lists this user's past conversations for the current scope (newest first). */
  listConversations: () => Promise<AiConversationSummary[]>;
  /** Lists this user's support requests for the current scope (newest first). */
  listSupportRequests: () => Promise<SupportRequestSummary[]>;
  /** Loads a past conversation's transcript and continues it. False = not loaded. */
  openConversation: (conversationId: string) => Promise<boolean>;
  /** Deletes one past conversation (clears the chat if it's the open one). False = failed. */
  deleteConversation: (conversationId: string) => Promise<boolean>;
  /** Aborts the in-flight stream but KEEPS the transcript (partial answer stays). */
  stopStreaming: () => void;
  /** Re-sends the last user message (dropping the failed turn). False = not accepted. */
  retryLast: () => boolean;
  /**
   * Sends a turn. `attachments` are base64 image blocks (the composer already
   * read the user's Files) shown in the user bubble and threaded to the model.
   * Returns false when the message was NOT accepted (in-flight / no scope).
   */
  sendMessage: (text: string, attachments?: ImageAttachment[]) => boolean;
  /** Set while a destructive tool call awaits the user's verdict. */
  pendingConfirmation: AiPendingAction | null;
  /**
   * Answers the pending confirmation. False = not accepted (nothing pending).
   * `confirmation` is what the ConfirmationCard itself built (W8: the
   * import's legal-basis tick) — handed to a confirm-gated client tool's
   * handler as its second argument, never derived from the tool input.
   */
  confirmPendingTool: (approved: boolean, confirmation?: AiClientConfirmation) => boolean;
};

const AiAssistantContext = createContext<AiAssistantContextType | undefined>(undefined);

export function useAiAssistant(): AiAssistantContextType {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) throw new Error("useAiAssistant must be used within AiAssistantProvider");
  return ctx;
}

/**
 * Safe variant: returns null when no provider is mounted. The builder bodies
 * are shared between event builders (wrapped in BuilderScopedAiAssistant) and
 * template-mode builders (no eventId → no AI provider), so they probe for the
 * context instead of assuming it.
 */
export function useAiAssistantOptional(): AiAssistantContextType | null {
  return useContext(AiAssistantContext) ?? null;
}

export function AiAssistantProvider({
  scope,
  available,
  chrome = true,
  ephemeralTranscript = false,
  mountSupportTools = true,
  children,
}: {
  /** null while the scope id is still resolving (e.g. cookie read). */
  scope: AiChatScope | null;
  available: boolean;
  /**
   * Render the provider's own chat surfaces (drawer / studio / bootstrap
   * overlay). Default true. The in-builder mount sets it false: the builder's
   * docked AiBuilderPanel is the only surface there, driven purely by this
   * context — the global drawer would otherwise mount over the builder.
   */
  chrome?: boolean;
  /**
   * Skip the per-scope TRANSCRIPT_CACHE entirely (no restore on mount, no
   * stash on unmount) AND leave the global composer draft untouched on
   * unmount. Set by the wizard mount: it NESTS inside the event-scoped
   * provider with the SAME scopeKey, so sharing the cache would restore the
   * drawer's chat into the wizard and clobber the drawer's cache on close —
   * a wizard interview is intentionally fresh + throwaway.
   */
  ephemeralTranscript?: boolean;
  /**
   * Mount the universal AiSupportTools registrar. Default true. A NESTED
   * provider (wizard) must pass false: the tool bridge is global and
   * last-mount-wins, so a second AiSupportTools would unregister
   * report_bug/escalate_to_human for the outer drawer when the wizard closes
   * (the outer copy registers once and never re-registers).
   */
  mountSupportTools?: boolean;
  children: React.ReactNode;
}) {
  const locale = useLocale();
  const { blockIfPreviewing } = usePreviewWriteGuard();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // Open flag from the cross-tree store (see ai-store.ts: it must survive
  // this provider's unmount/remount on a dashboard↔event shell swap).
  const open = useAiAvailabilityStore((s) => s.drawerOpen);

  // Stable identity of the scope for reset/dependency purposes.
  const scopeKey = scope === null ? "" : `${scope.kind}:${scope.id}`;

  const abortRef = useRef<AbortController | null>(null);
  // Synchronous re-entrancy guard: state.streaming only flips on the next
  // render, so two submits in the same tick would both pass a state-based
  // check and interleave two streams into one bubble. Set/cleared
  // imperatively around the stream lifetime. (Refs are NOT written during
  // render — that breaks under concurrent rendering.)
  const inFlightRef = useRef(false);
  const conversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    conversationIdRef.current = state.conversationId;
  }, [state.conversationId]);
  // Stable getter so the support tools read the live conversation id without
  // re-registering their bridge handlers each turn.
  const getConversationId = useCallback(() => conversationIdRef.current, []);

  // Latest sendMessage, so the once-registered openAiAssistant listener can
  // auto-send a priming prompt without re-subscribing every turn.
  const sendMessageRef = useRef<AiAssistantContextType["sendMessage"] | null>(null);

  // External entry point (Setup-guide row). HANDSHAKE: the listener calls
  // preventDefault() only when the assistant can actually open — dispatchers
  // check `defaultPrevented` to know the click was honored. Gating here (via
  // refs — the listener is registered once) also prevents the open-latch
  // bug: without it, a click while available=false stores open=true and the
  // drawer would slide in unprompted whenever availability later resolves.
  const canOpenRef = useRef(false);
  const canOpen = available && scope !== null;
  useEffect(() => {
    canOpenRef.current = canOpen;
    // Publish availability for the AppBar AI button, which mounts outside
    // this provider on event pages. Reset on unmount/scope-loss so a shell
    // switch never leaves a button that dispatches into the void. Headless
    // (in-builder) mounts have no AppBar button and must not touch the global
    // drawer state — skip.
    if (!chrome) return;
    useAiAvailabilityStore.getState().setAvailable(canOpen);
    return () => useAiAvailabilityStore.getState().setAvailable(false);
  }, [canOpen, chrome]);
  useEffect(() => {
    if (!chrome) return;
    const handleOpen = (e: Event) => {
      if (!canOpenRef.current) return;
      e.preventDefault();
      useAiAvailabilityStore.getState().setDrawerOpen(true);
      // Optional priming payload (Setup-guide "Discuss with Velo"): open the
      // drawer and either auto-send the prompt or drop it into the composer.
      const detail = (e as CustomEvent).detail as
        | { prompt?: string; autoSend?: boolean }
        | undefined;
      const prompt = detail?.prompt?.trim();
      if (!prompt) return;
      // autoSend can be refused (a stream is already in flight, or preview-as-
      // role) — fall back to the composer so the primer isn't lost silently.
      if (detail?.autoSend && sendMessageRef.current?.(prompt) === true) return;
      useAiAvailabilityStore.getState().setComposerDraft(prompt);
    };
    window.addEventListener("openAiAssistant", handleOpen);
    return () => window.removeEventListener("openAiAssistant", handleOpen);
  }, [chrome]);

  // Wizard→event handoff badge: arriving in an event whose create-wizard
  // stashed a handoff dots the AppBar AI button (the button lives outside
  // this provider, so the store bridges it — same pattern as `available`).
  // Seen = the drawer opened once on this event; the badge clears but the
  // handoff payload stays until the first message consumes it.
  const scopeEventIdForBadge = scope !== null && scope.kind === "event" ? scope.id : null;
  useEffect(() => {
    if (!chrome || !scopeEventIdForBadge) return;
    if (readVeloHandoff(scopeEventIdForBadge)) {
      useAiAvailabilityStore.getState().setHandoffBadgeEventId(scopeEventIdForBadge);
    }
    return () => useAiAvailabilityStore.getState().setHandoffBadgeEventId(null);
  }, [chrome, scopeEventIdForBadge]);
  useEffect(() => {
    if (!open || !scopeEventIdForBadge) return;
    const store = useAiAvailabilityStore.getState();
    if (store.handoffBadgeEventId === scopeEventIdForBadge) {
      store.setHandoffBadgeEventId(null);
    }
  }, [open, scopeEventIdForBadge]);

  // /create-event is immersive (the wizard owns the screen) — the shortcut
  // must not open the drawer there. Gating here (vs relying on the drawer's
  // auto-close effect) avoids a visible open-then-close flash. Ref for the
  // same stale-closure reason as canOpenRef.
  const isImmersiveRoute = isCreateEventRoute(useLocation().pathname);
  const immersiveRef = useRef(isImmersiveRoute);
  useEffect(() => {
    immersiveRef.current = isImmersiveRoute;
  }, [isImmersiveRoute]);

  // Global Cmd/Ctrl+J toggle — mirrors useUniversalSearch's Cmd+K mechanics
  // (e.repeat guard, preventDefault, document-level). Registered here, not
  // in the drawer: it must work while the drawer is closed. preventDefault
  // is mandatory — Firefox opens its downloads panel on Cmd+J otherwise.
  useEffect(() => {
    // Headless (in-builder) mounts don't own the global drawer chrome —
    // the builder's own AI button drives its panel. A ⌘J here would otherwise
    // toggle the shared drawerOpen flag, popping the drawer on the next event
    // page (the flag survives the shell swap by design).
    if (!chrome) return;
    const handleShortcut = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (!(e.ctrlKey || e.metaKey) || e.key !== "j") return;
      e.preventDefault();
      const store = useAiAvailabilityStore.getState();
      if (store.drawerOpen) {
        store.setDrawerOpen(false);
      } else if (canOpenRef.current && !immersiveRef.current) {
        store.setDrawerOpen(true);
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, [chrome]);

  // Scope switch (event→event, dashboard→event, …) keeps this provider
  // MOUNTED (only the Outlet is keyed), so without a reset the previous
  // scope's transcript + conversationId would leak into the new one — and
  // the stale conversationId would make every send 404 with
  // conversationNotFound.
  const prevScopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKeyRef.current !== scopeKey) {
      const prevKey = prevScopeKeyRef.current;
      prevScopeKeyRef.current = scopeKey;
      abortRef.current?.abort();
      discardDeltas(); // buffered tail belongs to the outgoing scope's stream
      inFlightRef.current = false;
      pendingClientToolRef.current = null;
      // The composer draft + attachments reset with the transcript they
      // belonged to (clearComposerAttachments revokes their object URLs).
      useAiAvailabilityStore.getState().setComposerDraft("");
      useAiAvailabilityStore.getState().clearComposerAttachments();
      useAiAvailabilityStore.getState().setTurnAttachments([]);
      // Stash the OUTGOING scope's transcript (refs hold the pre-reset state),
      // then restore the INCOMING scope's cached chat if we've seen it this
      // session — otherwise start fresh. This is what stops an event switch
      // from wiping the assistant history.
      if (prevKey && !ephemeralTranscript) {
        cacheTranscript(prevKey, messagesRef.current, conversationIdRef.current);
      }
      const cached =
        scopeKey && !ephemeralTranscript ? TRANSCRIPT_CACHE.get(scopeKey) : undefined;
      if (cached) {
        dispatch({
          type: "restore",
          messages: cached.messages,
          conversationId: cached.conversationId,
        });
      } else {
        dispatch({ type: "newConversation" });
      }
    }
    return () => abortRef.current?.abort();
  }, [scopeKey]);

  // The scopeKey effect above only covers switches WITHIN one mounted provider.
  // But the provider also REMOUNTS in two cases: a dashboard↔event shell swap,
  // and an event→event jump to a not-yet-cached event (EventLayoutShell renders
  // null for `event` for a frame, unmounting us). So this is the PRIMARY path
  // for many event↔event switches too: restore this scope's cache on mount, and
  // write the current transcript back on unmount — so the history survives both.
  useEffect(() => {
    if (ephemeralTranscript) return; // wizard mount: fresh in, throwaway out
    const mountKey = scopeKey;
    if (mountKey) {
      const cached = TRANSCRIPT_CACHE.get(mountKey);
      if (cached) {
        dispatch({
          type: "restore",
          messages: cached.messages,
          conversationId: cached.conversationId,
        });
      }
    }
    return () => {
      // Only stash a NON-empty transcript on unmount. (Empty would be a no-op
      // anyway, and guarding it keeps React StrictMode's dev mount→unmount→mount
      // cycle from deleting a just-restored cache before the real mount.)
      if (prevScopeKeyRef.current && messagesRef.current.length > 0) {
        cacheTranscript(
          prevScopeKeyRef.current,
          messagesRef.current,
          conversationIdRef.current,
        );
      }
    };
    // Mount/unmount only — refs read the latest values inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The draft dies when this provider unmounts (shell swap) — it belongs to
  // the transcript, which dies with the provider; without this an event-page
  // draft would surface in the company composer (review D-NEW-1). An
  // ephemeral (wizard) mount must NOT do this: it nests inside the event
  // provider, and wiping the GLOBAL draft on wizard close would erase text
  // the user had typed in the still-mounted drawer composer.
  useEffect(() => {
    if (ephemeralTranscript) return;
    return () => {
      useAiAvailabilityStore.getState().setComposerDraft("");
      useAiAvailabilityStore.getState().clearComposerAttachments();
      useAiAvailabilityStore.getState().setTurnAttachments([]);
    };
    // Mount-constant prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A client_tool pause stashed here executes AFTER its stream ends (the
  // backend ends the stream right after the frame — executing from within
  // onEvent would race the turnEnd bookkeeping).
  const pendingClientToolRef = useRef<AiPendingAction | null>(null);
  // toolUseId → toolName, captured at tool_use so a tool_result (which carries
  // only the id) can name the tool that just mutated data — see the
  // `veltric:ai-data-mutated` dispatch that lets open pages refetch live.
  const toolNamesRef = useRef<Map<string, string>>(new Map());
  // Highest credit total seen for the CURRENT user message, or null before its
  // first frame. Lives on the provider, not in runStream: a paused turn
  // resumes over a second request, and a per-request counter would treat that
  // request's opening frame as "the prepaid one" and skip the refetch on a
  // message that had already spent several credits.
  const creditTotalRef = useRef<number | null>(null);
  // Breaks the runStream → executeClientTool → reenter → runStream cycle.
  const executeClientToolRef = useRef<
    ((action: AiPendingAction, confirmation?: AiClientConfirmation) => Promise<void>) | null
  >(null);
  // pendingAction mirrored for the stable confirmPendingTool callback.
  const pendingActionRef = useRef(state.pendingAction);
  useEffect(() => {
    pendingActionRef.current = state.pendingAction;
  }, [state.pendingAction]);
  // scope mirrored for the stable runStream closure (deps []).
  const scopeRef = useRef(scope);
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);

  // ── Delta coalescing ──────────────────────────────────────────────────
  // One React dispatch per SSE chunk made the whole render pipeline (markdown
  // re-parse, transcript regroup, context fan-out, scroll reflow) run per
  // token. Buffer text deltas and flush at most every 50ms (setTimeout, not
  // rAF: deterministic under fake timers and fires in background tabs).
  const deltaBufRef = useRef("");
  const deltaTimerRef = useRef<number | null>(null);

  /** Dispatch whatever is buffered NOW (ordering barrier + turn end). */
  const flushDeltas = useCallback(() => {
    if (deltaTimerRef.current !== null) {
      window.clearTimeout(deltaTimerRef.current);
      deltaTimerRef.current = null;
    }
    if (deltaBufRef.current) {
      const text = deltaBufRef.current;
      deltaBufRef.current = "";
      dispatch({ type: "delta", text });
    }
  }, []);

  /** Clear WITHOUT dispatching — scope switch / conversation switch. */
  const discardDeltas = useCallback(() => {
    if (deltaTimerRef.current !== null) {
      window.clearTimeout(deltaTimerRef.current);
      deltaTimerRef.current = null;
    }
    deltaBufRef.current = "";
  }, []);

  // Unmount: drop ≤50ms of tail text; the transcript cache reads messagesRef.
  useEffect(() => () => discardDeltas(), [discardDeltas]);

  /**
   * Shared lifecycle for the initial chat stream AND tool-result re-entries:
   * event fan-out, error mapping, turn bookkeeping, deferred client-tool
   * execution. `start` opens the actual SSE call with the provided onEvent.
   */
  const runStream = useCallback(
    (
      controller: AbortController,
      start: (onEvent: (ev: AiStreamEvent) => void) => Promise<void>,
    ) => {
      const onEvent = (ev: AiStreamEvent) => {
        // A chunk continuation can run AFTER this stream was aborted (stop /
        // scope switch). Pre-coalescing that meant a stray delta in the
        // just-stopped bubble; with the 50ms buffer it could leak old-stream
        // text into the NEXT stream's bubble — drop post-abort events cold
        // (stopStreaming already flushed the legitimate tail synchronously).
        if (controller.signal.aborted) return;
        // Ordering barrier: buffered text must land in the CURRENT bubble
        // before any tool/pause/error event finalizes it and opens a new one.
        if (ev.type !== "text_delta") flushDeltas();
        switch (ev.type) {
          case "conversation":
            if (ev.conversationId) {
              // Write the ref SYNCHRONOUSLY (not only via the state effect):
              // a client tool (e.g. escalate_to_human on the first turn) runs in
              // runStream's deferred .finally, which can execute before React
              // flushes the conversationId effect. Writing here guarantees
              // getConversationId() / reenter() see the id immediately, so the
              // support ticket is linked to the transcript and the re-entry
              // never drops for lack of a conversation id.
              conversationIdRef.current = ev.conversationId;
              dispatch({ type: "conversation", conversationId: ev.conversationId });
              // Wizard→event handoff consumed: the conversation now owns the
              // brief server-side — drop the one-shot payload (no-op when none
              // was pending; the badge cleared on drawer open already).
              const sc = scopeRef.current;
              if (sc?.kind === "event") clearVeloHandoff(sc.id);
            }
            break;
          case "text_delta":
            if (ev.text) {
              deltaBufRef.current += ev.text;
              if (deltaTimerRef.current === null) {
                deltaTimerRef.current = window.setTimeout(flushDeltas, 50);
              }
            }
            break;
          case "tool_use":
            if (ev.toolUseId) toolNamesRef.current.set(ev.toolUseId, ev.toolName ?? "");
            dispatch({
              type: "toolUse",
              toolMessageId: crypto.randomUUID(),
              assistantMessageId: crypto.randomUUID(),
              toolUseId: ev.toolUseId ?? "",
              toolName: ev.toolName ?? "",
            });
            break;
          case "tool_result": {
            const failed = !!ev.errorCode;
            dispatch({
              type: "toolStatus",
              toolUseId: ev.toolUseId ?? "",
              status: failed
                ? ev.errorCode === "declined"
                  ? "declined"
                  : "error"
                : "done",
            });
            // A SERVER tool ran to completion and may have mutated event data the
            // browser never loaded (it executed loopback, not via the FE). Tell
            // any open page so it can refetch live instead of needing a manual
            // reload. Decoupled via a window event — this provider has no page
            // context. Consumers filter by toolName + eventId. Skip declined/
            // errored runs (no mutation happened).
            const toolName = (ev.toolUseId && toolNamesRef.current.get(ev.toolUseId)) || "";
            if (ev.toolUseId) toolNamesRef.current.delete(ev.toolUseId);
            if (!failed && toolName) {
              const sc = scopeRef.current;
              const mutScopeEvtId = sc && sc.kind === "event" ? sc.id : null;
              window.dispatchEvent(
                new CustomEvent(AI_DATA_MUTATED_EVENT, {
                  detail: { toolName, eventId: mutScopeEvtId },
                }),
              );
            }
            break;
          }
          case "tool_confirmation_required":
          case "client_tool": {
            const action: AiPendingAction = {
              kind: ev.type === "client_tool" ? "client" : "confirm",
              toolUseId: ev.toolUseId ?? "",
              toolName: ev.toolName ?? "",
              toolInputJson: ev.toolInputJson ?? "{}",
            };
            if (action.kind === "client") pendingClientToolRef.current = action;
            dispatch({ type: "pause", action });
            break;
          }
          case "generation_progress":
            // P7 multi-page websites: per-page progress during the
            // minutes-long sequential generation — rendered in the ToolRow
            // and the studio pane's generating overlay.
            if (ev.toolUseId && ev.page && ev.total) {
              dispatch({
                type: "toolProgress",
                toolUseId: ev.toolUseId,
                page: ev.page,
                total: ev.total,
                title: ev.title ?? "",
              });
            }
            break;
          case "background_job": {
            // A SERVER tool submitted a tracked job (guest export / bulk send).
            // The loopback submit is invisible to the browser, so hand the job
            // off to the Downloads/progress tracker. Decoupled via a window
            // event (this provider has no event-name context and isn't mounted
            // under BackgroundJobsProvider's job-row consumers) — AiBackgroundJob
            // Bridge, mounted next to PBackgroundJobs, calls addJob.
            // eventId is backend-authoritative (the job's own event); fall back
            // to the live event scope only if an older backend omits it. These
            // tools are event-scoped, so the company scope never carries them.
            const sc = scopeRef.current;
            const scopeEvtId = sc && sc.kind === "event" ? sc.id : null;
            const evtId = ev.eventId ?? scopeEvtId;
            if (ev.backgroundJobId && ev.taskType && evtId) {
              window.dispatchEvent(
                new CustomEvent("veltric:ai-background-job", {
                  detail: {
                    jobId: ev.backgroundJobId,
                    taskType: ev.taskType,
                    eventId: evtId,
                  },
                }),
              );
            }
            break;
          }
          case "credits_charged": {
            if (typeof ev.creditCost !== "number") break;
            dispatch({ type: "creditCost", creditCost: ev.creditCost });
            // The first frame of a message only announces what the up-front
            // debit already took, and the turn-end reconcile covers that. Any
            // GROWTH after it means the tool loop spent more — exactly when a
            // minutes-long turn must not keep showing a stale ring.
            const seen = creditTotalRef.current;
            creditTotalRef.current = ev.creditCost;
            if (seen !== null && ev.creditCost > seen) reconcileAiCredits();
            break;
          }
          case "error":
            dispatch({ type: "turnError", errorCode: ev.errorCode ?? "aiUnavailable" });
            break;
          // ping ignored; message_stop handled by stream completion below
        }
      };

      start(onEvent)
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          // Keep any partial text next to the error bubble.
          flushDeltas();
          const code = err instanceof Error ? err.message : "aiUnavailable";
          if (code === "unauthorized") {
            // Mirror the axios interceptor: session is gone, trigger the
            // app-level logout flow instead of a misleading chat error.
            window.dispatchEvent(new CustomEvent("veltric:auth-lost"));
          }
          dispatch({
            type: "turnError",
            errorCode: PASSTHROUGH_ERROR_CODES.has(code) ? code : "aiUnavailable",
          });
        })
        .finally(() => {
          // Flush BEFORE the newer-stream guard: an aborted stream's own tail
          // text is legitimately part of its transcript (stopStreaming keeps
          // partial answers by design), and resolved-stream tests see the full
          // content synchronously.
          flushDeltas();
          // A newer stream may have started after this one was aborted
          // (scope switch / new conversation) — its guard and flags are not
          // ours to clear anymore.
          if (abortRef.current !== controller) return;
          const clientAction = controller.signal.aborted ? null : pendingClientToolRef.current;
          const willRunClientTool = !!clientAction && !!executeClientToolRef.current;
          // Keep the turn IN-FLIGHT across a deferred client tool's handler
          // window: clearing inFlightRef here would let the user submit a new
          // message while the handler runs, and the tool-result re-entry would
          // then be rejected — its result dropped and the conversation silently
          // diverged from the server. executeClientTool hands inFlightRef off to
          // reenter synchronously once the handler resolves. Cleared now only
          // when there is NO client tool to continue into.
          if (!willRunClientTool) inFlightRef.current = false;
          // prune only on a NATURAL end: a pause/tool-only step leaves an
          // empty trailing bubble that must not render as "stopped"; a
          // user-stop keeps it (that marker is the point).
          dispatch({ type: "turnEnd", prune: !controller.signal.aborted });
          // The turn settled — pull the authoritative credit balance. Without
          // this the balance query (staleTime 60s, no focus refetch) is fetched
          // once when the drawer mounts and then FROZEN for the whole session:
          // users watched a cheerful "1000 left" while spending down to zero.
          // Sits after the newer-stream guard, so an aborted-and-superseded
          // stream doesn't fire a second refetch on top of its successor's.
          reconcileAiCredits();
          if (willRunClientTool) {
            pendingClientToolRef.current = null;
            void executeClientToolRef.current!(clientAction!);
          }
        });
    },
    [flushDeltas],
  );

  /**
   * Surface marker for the backend's one-line builder hint — read at call
   * time from the store (the chat may be driven from an in-builder panel).
   */
  const currentSurface = (): string | undefined => {
    const store = useAiAvailabilityStore.getState();
    // A creation wizard is a full-screen overlay — while one is mounted it IS
    // the user's context, so it wins over any builder surface underneath.
    if (store.wizardSurface) return `wizard:${store.wizardSurface}`;
    // The email/document/website builders drive the chat from their own panel
    // and need the surface-gated read/apply tools.
    return store.builderSurface ? `builder:${store.builderSurface}` : undefined;
  };

  // Best-effort follow-up chips: once a turn has fully settled (not streaming,
  // no pending pause), fetch a few next-step suggestions for the just-answered
  // assistant message and attach them BY ID. Decoupled from the SSE turn on
  // purpose — the composer is never held open while the model thinks. Aborts on
  // a scope change or when a newer turn starts; a failure/empty result simply
  // leaves the bubble without chips. Writes [] (not undefined) so it fires once.
  useEffect(() => {
    if (state.streaming || state.pendingAction || scope === null) return;
    // Chips render only in the drawer chat or a builder/wizard panel — with
    // none of those visible the fetch (an extra model call server-side) is
    // pure waste (auto-resume with a closed drawer, drawer closed mid-turn).
    // Deferral is safe: suggestions stay undefined, and `open` is a dep, so
    // opening the drawer later fetches for the same message id.
    const store = useAiAvailabilityStore.getState();
    // A wizard panel shows only its OWN (ephemeral) provider's chips — the outer
    // company provider must not fetch for it (a model call while the create
    // wizard's Velo may be off; PLAN2 O2, P2-O-17 / D-6).
    const surfaceVisible =
      open || store.builderSurface !== null || (ephemeralTranscript && store.wizardSurface !== null);
    if (!surfaceVisible) return;
    const last = state.messages[state.messages.length - 1];
    if (
      !last ||
      last.role !== "assistant" ||
      !last.content ||
      last.errorCode ||
      last.suggestions !== undefined
    )
      return;
    const conversationId = conversationIdRef.current;
    if (!conversationId) return;

    const controller = new AbortController();
    const targetId = last.id;
    fetchAiSuggestions(
      scope,
      { conversationId, locale, surface: currentSurface() },
      controller.signal,
    )
      .then((items) => dispatch({ type: "suggestions", messageId: targetId, items }))
      .catch(() => {
        // Best-effort: abort / network / 4xx all mean "no chips".
      });
    return () => controller.abort();
    // currentSurface + builder/wizard surface read the store at call time; the
    // surfaces are mount-constant for this provider's lifetime, so only the
    // drawer bit needs to be reactive (late open → fetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.streaming, state.pendingAction, state.messages, scope, locale, open, ephemeralTranscript]);

  /** Resumes a paused turn via the tool-result endpoint (plan §4.5.2). */
  const reenter = useCallback(
    (body: Omit<AiToolResultBody, "conversationId" | "locale">): boolean => {
      // Same read-only-preview bypass as sendMessage — the resume stream skips
      // the axios client too, so block it (a paused tool must not resume as
      // admin during a read-only preview).
      if (blockIfPreviewing()) return false;
      const conversationId = conversationIdRef.current;
      // Both scopes can resume now: the event chat has the full tool set, the
      // company/dashboard chat has the universal support tools (report_bug /
      // escalate_to_human). streamAiToolResult routes to the right endpoint.
      if (inFlightRef.current || scope === null || !conversationId) {
        return false;
      }
      inFlightRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;
      dispatch({ type: "turnContinue", assistantMessageId: crypto.randomUUID() });
      runStream(controller, (onEvent) =>
        streamAiToolResult(
          scope,
          {
            ...body,
            conversationId,
            locale,
            surface: currentSurface(),
          },
          onEvent,
          controller.signal,
        ),
      );
      return true;
    },
    // scope is identified by scopeKey (object identity churns per render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey, locale, runStream, blockIfPreviewing],
  );

  const confirmPendingTool = useCallback(
    (approved: boolean, confirmation?: AiClientConfirmation): boolean => {
      const action = pendingActionRef.current;
      if (!action || action.kind === "client") return false;
      dispatch({ type: "pauseResolved" });
      dispatch({
        type: "toolStatus",
        toolUseId: action.toolUseId,
        status: approved ? "running" : "declined",
      });
      if (action.kind === "confirm") {
        return reenter({ toolUseId: action.toolUseId, approved });
      }
      // clientConfirm — the handler never ran. Approve: execute now (the
      // clientConfirm kind skips the gate on re-entry). Decline: never run the
      // handler; report a soft declined result so the model doesn't retry
      // (mirrors the server confirm-decline wording).
      if (approved) {
        // Parity with the auto-run lane: keep the turn in-flight across the
        // handler window so a concurrent user submit can't orphan the result.
        inFlightRef.current = true;
        // The card's own confirmation object (W8) rides along to the handler;
        // the server-confirm lane above has no handler and ignores it.
        void executeClientToolRef.current?.(action, confirmation);
        return true;
      }
      return reenter({
        toolUseId: action.toolUseId,
        resultJson: JSON.stringify({
          ok: false,
          declined: true,
          note: "The user DECLINED this action. Do not retry it unless the user asks again.",
        }),
      });
    },
    [reenter],
  );

  /**
   * Executes a client tool against the page-registered handler (plan §4.5.3)
   * and re-enters with its result. Handlers RETURN soft errors as data (the
   * model self-corrects); thrown Error.message becomes the errorCode lane
   * (pageNotOpen / clientToolTimeout / hard failures).
   */
  const executeClientTool = useCallback(
    async (action: AiPendingAction, confirmation?: AiClientConfirmation) => {
      let body: Omit<AiToolResultBody, "conversationId" | "locale">;
      const handler = useAiToolBridge.getState().handlers[action.toolName];
      if (!handler) {
        body = { toolUseId: action.toolUseId, errorCode: "pageNotOpen" };
      } else {
        let input: unknown = {};
        try {
          input = JSON.parse(action.toolInputJson) as unknown;
        } catch {
          // malformed input — hand the handler an empty object; its own
          // validation produces the actionable error payload
        }
        // Confirm-gated client tool: pause on the ConfirmationCard INSTEAD of
        // executing — no handler call, no timeout (the user may ponder
        // indefinitely). Approval re-enters here with kind "clientConfirm",
        // which skips this gate. Abandonment (conversation switch/unmount)
        // deliberately sends NO decline — the same silent-abandon semantic as
        // the server confirm lane; the backend strips the dangling tool_use
        // on the next user message.
        if (action.kind === "client") {
          const gate = useAiToolBridge.getState().confirmGates[action.toolName];
          const needsConfirm = typeof gate === "function" ? gate(input) : gate === true;
          if (needsConfirm) {
            inFlightRef.current = false;
            dispatch({ type: "pause", action: { ...action, kind: "clientConfirm" } });
            return;
          }
        }
        try {
          // Most client tools mutate page-local UI state and resolve instantly,
          // so the default budget is tight. A few do real, slow, SIDE-EFFECTING
          // work (parsing a large spreadsheet + submitting an import job); a
          // premature timeout there tells the model the call FAILED while the
          // job actually submits — risking a duplicate import on the model's
          // retry. Give those a generous budget instead.
          const timeoutMs =
            LONG_RUNNING_CLIENT_TOOLS[action.toolName] ?? CLIENT_TOOL_TIMEOUT_MS;
          let timer: number | undefined;
          try {
            const result = await Promise.race([
              // `confirmation` only ever comes from confirmPendingTool (the
              // card); the auto-run lane from runStream passes none.
              Promise.resolve(
                confirmation === undefined ? handler(input) : handler(input, confirmation),
              ),
              new Promise<never>((_, reject) => {
                timer = window.setTimeout(
                  () => reject(new Error("clientToolTimeout")),
                  timeoutMs,
                );
              }),
            ]);
            body = {
              toolUseId: action.toolUseId,
              resultJson: JSON.stringify(result ?? { ok: true }),
            };
          } finally {
            // Clear the loser timer so it can't fire against the settled race.
            if (timer !== undefined) window.clearTimeout(timer);
          }
        } catch (err) {
          body = {
            toolUseId: action.toolUseId,
            errorCode:
              err instanceof Error && err.message
                ? err.message.slice(0, 100)
                : "clientToolFailed",
          };
        }
      }
      dispatch({ type: "pauseResolved" });
      dispatch({
        type: "toolStatus",
        toolUseId: action.toolUseId,
        status: body.errorCode ? "error" : "done",
      });
      // Client-lane parity with the server-tool dispatch in runStream: a
      // client tool that mutated server state (create/generate template,
      // document, …) must also wake AiDataSyncBridge — this lane never fired
      // the event, so mounted lists sat stale until a manual reload. Local-only
      // tools map to [] in TOOL_DOMAINS, so the bridge no-ops for them.
      if (!body.errorCode) {
        const sc = scopeRef.current;
        window.dispatchEvent(
          new CustomEvent(AI_DATA_MUTATED_EVENT, {
            detail: {
              toolName: action.toolName,
              eventId: sc && sc.kind === "event" ? sc.id : null,
            },
          }),
        );
      }
      // The turn was kept in-flight across the handler window (see runStream's
      // finally). Hand it off to reenter by clearing the guard synchronously —
      // reenter immediately re-sets it, so no window opens for a double-submit.
      // A failed re-entry (scope switched away mid-execution) is fine to drop:
      // inFlightRef stays false and the backend strips the dangling tool_use on
      // the next turn.
      inFlightRef.current = false;
      reenter(body);
    },
    [reenter],
  );
  useEffect(() => {
    executeClientToolRef.current = executeClientTool;
  }, [executeClientTool]);

  const sendMessage = useCallback(
    (text: string, attachments?: ImageAttachment[]): boolean => {
      // Read-only "preview as role": the AI stream bypasses the axios client
      // (raw fetch, cookie auth — see AiClient), so it would run as the ADMIN,
      // not the previewed role, and its write-tools (create guest, send email,
      // …) would EXECUTE FOR REAL. Block the send and explain instead.
      if (blockIfPreviewing()) return false;
      const message = text.trim();
      const images = attachments && attachments.length ? attachments : undefined;
      // Send is allowed with text OR at least one image (image-only turns).
      if ((!message && !images) || inFlightRef.current || scope === null) return false;
      inFlightRef.current = true;

      const controller = new AbortController();
      abortRef.current = controller;
      // A new user message starts a new count, whatever the last one cost.
      creditTotalRef.current = null;

      dispatch({
        type: "turnStart",
        userMessageId: crypto.randomUUID(),
        assistantMessageId: crypto.randomUUID(),
        content: message,
        images,
      });

      // Wizard→event handoff: the FIRST message of a fresh event conversation
      // carries the wizard's composed brief so the backend pins it on the new
      // conversation (system-prompt context from turn one). One-shot — the
      // payload is cleared when the conversation frame confirms the id (so a
      // failed send keeps it for the retry).
      const handoff =
        scope.kind === "event" && !conversationIdRef.current
          ? readVeloHandoff(scope.id)
          : null;
      const handoffBrief = handoff ? composeVeloHandoffBrief(handoff) : "";

      runStream(controller, (onEvent) =>
        streamAiChat(
          scope,
          {
            conversationId: conversationIdRef.current ?? undefined,
            message,
            locale,
            surface: currentSurface(),
            // Only present when the user attached images — a plain text turn
            // sends a byte-identical body (no backend-version coupling).
            ...(images ? { images } : {}),
            ...(handoffBrief ? { handoffBrief } : {}),
          },
          onEvent,
          controller.signal,
        ),
      );
      return true;
    },
    // scope is identified by scopeKey (object identity churns per render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey, locale, runStream, blockIfPreviewing],
  );

  const openDrawer = useCallback(
    () => useAiAvailabilityStore.getState().setDrawerOpen(true),
    [],
  );
  const closeDrawer = useCallback(
    () => useAiAvailabilityStore.getState().setDrawerOpen(false),
    [],
  );
  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    // User-stop keeps the partial answer — land the buffered tail now.
    flushDeltas();
    // Same reasoning as newConversation: the aborted stream's finally would
    // clear these a microtask later — doing it here makes the next send
    // possible immediately. turnEnd is idempotent and keeps the transcript.
    inFlightRef.current = false;
    // Drop any client tool stashed by the aborted turn. Without this, the
    // NEXT stream's finally would pick up this stale action and execute it
    // (mutating the page + re-entering with a toolUseId that belongs to the
    // stopped turn — the backend then 400s noPendingToolUse). The stopped
    // turn's own dangling tool_use is harmless: the next normal user message
    // makes the server-side history repair strip it.
    pendingClientToolRef.current = null;
    dispatch({ type: "pauseResolved" });
    dispatch({ type: "turnEnd" });
  }, []);
  // Messages mirrored into a ref so retryLast stays referentially stable
  // (it's passed to memo'd bubbles — a per-message identity churn would
  // defeat the streaming re-render optimization).
  const messagesRef = useRef(state.messages);
  useEffect(() => {
    messagesRef.current = state.messages;
  }, [state.messages]);
  const retryLast = useCallback((): boolean => {
    if (inFlightRef.current) return false;
    let lastUser: AiMessage | undefined;
    for (let i = messagesRef.current.length - 1; i >= 0; i--) {
      if (messagesRef.current[i].role === "user") {
        lastUser = messagesRef.current[i];
        break;
      }
    }
    if (!lastUser) return false;
    // Both dispatches land in order within one batch: drop the failed
    // user+assistant pair, then turnStart re-appends a fresh pair (carrying
    // the same image attachments so a retried turn still reaches the model).
    dispatch({ type: "dropLastTurn" });
    return sendMessage(lastUser.content, lastUser.images);
  }, [sendMessage]);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);
  const newConversation = useCallback(() => {
    abortRef.current?.abort();
    discardDeltas(); // buffered tail belongs to the cleared conversation
    // The aborted stream's finally still sees abortRef.current === its own
    // controller and will clear the flag too — this just makes the next
    // send possible immediately instead of a microtask later.
    inFlightRef.current = false;
    pendingClientToolRef.current = null;
    // Explicitly clearing the chat must drop this scope's cached transcript too,
    // else switching away and back would resurrect the cleared history.
    // A wizard's (ephemeral) transcript is never cached — and must not delete
    // the outer drawer's cached chat of the same scope key (D-13).
    if (prevScopeKeyRef.current && !ephemeralTranscript) TRANSCRIPT_CACHE.delete(prevScopeKeyRef.current);
    // Signal a fresh conversation so cross-turn client-tool state resets (e.g.
    // AiImportTools' staged spreadsheet) and the last turn's upload sources drop —
    // otherwise a file staged in the old chat could be imported into the new one.
    useAiAvailabilityStore.getState().bumpChatReset();
    dispatch({ type: "newConversation" });
  }, [ephemeralTranscript]);

  const listConversations = useCallback(async (): Promise<AiConversationSummary[]> => {
    const sc = scopeRef.current;
    if (!sc) return [];
    // Let a real failure propagate so the panel can show its error state — a
    // network/500 must not be indistinguishable from "no chats yet".
    return listAiConversations(sc);
  }, []);

  const listSupportRequests = useCallback(async (): Promise<SupportRequestSummary[]> => {
    const sc = scopeRef.current;
    if (!sc) return [];
    try {
      return await listAiSupportRequests(sc);
    } catch {
      return [];
    }
  }, []);

  const openConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const sc = scopeRef.current;
      if (!sc) return false;
      let dto;
      try {
        dto = await getAiConversation(sc, conversationId);
      } catch {
        return false;
      }
      // Re-check scope AFTER the await: the provider stays mounted across an
      // event→event (or →company) switch, so a scope change mid-fetch would
      // otherwise splice conversation A's transcript into scope B (and abort
      // B's live turn). Bail if we're no longer in the scope we fetched for.
      const now = scopeRef.current;
      if (!now || now.kind !== sc.kind || now.id !== sc.id) return false;
      // A payload that is not an array (an older or broken response) is a
      // failed load — checked BEFORE the abort below, so a live turn survives
      // it and the history panel shows its error. Never a throw: the
      // auto-resume calls this fire-and-forget (an unhandled rejection).
      if (!Array.isArray(dto)) return false;
      // Abort any in-flight turn and reset transient guards (mirrors
      // newConversation) before swapping the transcript out from under it.
      abortRef.current?.abort();
      discardDeltas(); // buffered tail belongs to the replaced transcript
      inFlightRef.current = false;
      pendingClientToolRef.current = null;
      // A null row is skipped, not a throw.
      const rows = dto.filter((m) => m !== null && typeof m === "object");
      const messages: AiMessage[] = rows.map((m) => ({
        id: crypto.randomUUID(),
        role: m.role,
        content: m.content,
        // What the turn cost is the one thing a customer can audit a charge
        // against, and the frame that first carried it is gone the moment the
        // stream closed. Without this the badge vanished on every reload —
        // including the auto-resume that runs whenever the drawer reopens.
        ...(typeof m.messageCredits === "number"
          ? { creditCost: m.messageCredits, restored: true }
          : {}),
      }));
      // Loading a different conversation is a reset for cross-turn client-tool
      // state (e.g. a staged import file must not carry across).
      useAiAvailabilityStore.getState().bumpChatReset();
      // Write the ref synchronously (not only via the state effect) so a send
      // right after this resolves threads onto the restored conversation —
      // mirrors the "conversation" SSE handler's rationale.
      conversationIdRef.current = conversationId;
      dispatch({ type: "restore", messages, conversationId });
      // Keep the in-session cache in step so a scope bounce restores THIS chat.
      // Never from a wizard (ephemeral) mount: the cache key is shared with the
      // outer drawer of the same scope (A1-O-9 — D-13 relies on it).
      if (prevScopeKeyRef.current && !ephemeralTranscript) {
        cacheTranscript(prevScopeKeyRef.current, messages, conversationId);
      }
      return true;
    },
    [ephemeralTranscript],
  );

  const deleteConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const sc = scopeRef.current;
      if (!sc) return false;
      try {
        await deleteAiConversation(sc, conversationId);
      } catch {
        return false;
      }
      // If the deleted chat is the one currently open, clear it — continuing a
      // deleted conversation would 404 on the next send. Conversation ids are
      // globally unique, so a scope switch mid-await can't false-positive here.
      if (conversationIdRef.current === conversationId) newConversation();
      return true;
    },
    [newConversation],
  );

  // Auto-resume the most recent conversation when the drawer is opened on a
  // scope whose chat is empty — covers a full page reload (the in-memory cache
  // is gone, so without this the reopened drawer shows a blank chat). Runs at
  // most ONCE per scope per mount (a Set guard): so explicitly clicking "new
  // conversation" — which empties the chat WITHOUT changing scope — is NOT
  // undone by a re-resume. Gated on `open` so a closed drawer costs no fetch.
  const autoResumedScopesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // Wizard mounts never resume a stored chat (PLAN2 O2: Velo on is derived
    // from the transcript — a resumed chat would switch the create view on).
    if (ephemeralTranscript) return;
    const key = scopeKey;
    if (!key || !open || !available) return;
    if (autoResumedScopesRef.current.has(key)) return;
    autoResumedScopesRef.current.add(key);
    // If the in-session cache holds this scope's chat, the mount/scope-switch
    // effect restores it — don't fetch/overwrite. Checking the synchronous
    // module cache (not messagesRef, which lags a restore dispatch by a render)
    // avoids clobbering a just-restored transcript on a within-session bounce.
    // After a FULL RELOAD the module cache is empty, so this is the path that
    // refills the reopened drawer with the latest server-side conversation.
    const cached = TRANSCRIPT_CACHE.get(key);
    if (cached && cached.messages.length > 0) return;
    // No cancel-on-cleanup flag on purpose: under StrictMode's dev
    // mount→cleanup→mount, a flag would cancel the first attempt while the Set
    // guard skips the second, leaving no resume. Correctness instead rides on
    // the explicit guards below (+ openConversation's own post-await scope
    // re-check), which drop the result if anything changed during the fetch.
    void (async () => {
      let list;
      try {
        list = await listConversations();
      } catch {
        // Transient failure — un-mark so re-opening the drawer retries (the
        // history menu is still available manually meanwhile).
        autoResumedScopesRef.current.delete(key);
        return;
      }
      if (list.length === 0) return;
      // Re-check we're still on the same empty scope (the user may have started
      // typing/sending, switched away, or CLOSED the drawer during the fetch).
      const sc = scopeRef.current;
      if (
        !sc ||
        `${sc.kind}:${sc.id}` !== key ||
        !useAiAvailabilityStore.getState().drawerOpen ||
        messagesRef.current.length > 0 ||
        conversationIdRef.current ||
        inFlightRef.current
      ) {
        return;
      }
      void openConversation(list[0].conversationId);
    })();
  }, [scopeKey, open, available, listConversations, openConversation, ephemeralTranscript]);

  const scopeKind = scope === null ? null : scope.kind;
  const scopeEventId = scope !== null && scope.kind === "event" ? scope.id : null;

  const pendingConfirmation =
    state.pendingAction?.kind === "confirm" || state.pendingAction?.kind === "clientConfirm"
      ? state.pendingAction
      : null;

  const value = useMemo<AiAssistantContextType>(
    () => ({
      open,
      messages: state.messages,
      streaming: state.streaming,
      available,
      scopeKind,
      scopeEventId,
      getConversationId,
      openDrawer,
      closeDrawer,
      newConversation,
      listConversations,
      listSupportRequests,
      openConversation,
      deleteConversation,
      stopStreaming,
      retryLast,
      sendMessage,
      pendingConfirmation,
      confirmPendingTool,
    }),
    [open, state.messages, state.streaming, available, scopeKind, scopeEventId, getConversationId, openDrawer, closeDrawer, newConversation, listConversations, listSupportRequests, openConversation, deleteConversation, stopStreaming, retryLast, sendMessage, pendingConfirmation, confirmPendingTool],
  );

  return (
    <AiAssistantContext.Provider value={value}>
      {children}
      {/* Universal support tools (report_bug / escalate_to_human) — registered
          in EVERY scope (event, company, builder) so the bot can file a bug or
          reach a human anywhere. Renders nothing. NESTED mounts (wizard) skip
          this — see the mountSupportTools prop doc. */}
      {mountSupportTools && available && scope !== null && <AiSupportTools />}
      {/* Headless (in-builder) mounts skip ALL provider-owned chrome — the
          docked builder panel is the only surface. */}
      {chrome && available && scope !== null && <AiAssistantDrawer />}
    </AiAssistantContext.Provider>
  );
}

/**
 * Event-page mount: must sit inside EventProvider + CapabilitiesProvider.
 * eventId comes from the ROUTE — NOT from `useEventContext().event.eventId`,
 * which is usually undefined (EventLayoutShell's event query doesn't request
 * the eventId field, and the repo only maps requested fields).
 */
export function EventScopedAiAssistant({ children }: { children: React.ReactNode }) {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const { can } = useCapabilities();
  const { progress } = useOnboarding();
  useCompanyRoleCacheWriter(readCompanyIdSync(), progress?.companyRole);
  const scope = useMemo<AiChatScope | null>(
    () => (eventId ? { kind: "event", id: eventId } : null),
    [eventId],
  );
  return (
    <AiAssistantProvider scope={scope} available={can("ai:use")}>
      {children}
    </AiAssistantProvider>
  );
}

/**
 * In-builder mount for the email/document builders. These routes live OUTSIDE
 * EventLayoutShell (App.tsx) — no EventScopedAiAssistant, no drawer — so this
 * wraps the editor in a HEADLESS provider (chrome=false): the docked
 * AiBuilderPanel is the only surface. It publishes `builder:<kind>` as the
 * surface for as long as it's mounted, so the backend exposes the surface-gated
 * read/apply tools. Event-scope only (AI chat is event-bound); template-mode
 * builders have no eventId and simply don't render this.
 *
 * `available` is the page's edit gate (role/archived) — the server's ai:use
 * check stays authoritative for the actual chat calls.
 */
export function BuilderScopedAiAssistant({
  kind,
  available,
  children,
}: {
  kind: BuilderSurface;
  available: boolean;
  children: React.ReactNode;
}) {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const scope = useMemo<AiChatScope | null>(
    () => (eventId ? { kind: "event", id: eventId } : null),
    [eventId],
  );
  useEffect(() => {
    useAiAvailabilityStore.getState().setBuilderSurface(kind);
    return () => useAiAvailabilityStore.getState().setBuilderSurface(null);
  }, [kind]);
  return (
    <AiAssistantProvider scope={scope} available={available} chrome={false}>
      {children}
    </AiAssistantProvider>
  );
}

/**
 * Creation-wizard mount (slotting offering / email template / document). A
 * HEADLESS provider NESTED inside the ambient event-scoped provider: the
 * wizard's docked WizardVeloPanel is the only surface. Publishes
 * `wizard:<kind>` (which wins over any builder surface) so the backend
 * exposes the read/apply wizard-state tools + the interview hint. The
 * transcript is ephemeral (fresh per wizard open, thrown away on close) and
 * support tools stay with the OUTER provider — see the matching props.
 * Event-scope only; template-mode wizards have no eventId and don't render
 * this. Also closes the drawer on mount: the wizard covers the screen, and a
 * drawer streaming underneath would race the wizard surface.
 */
export function WizardScopedAiAssistant({
  kind,
  available,
  children,
}: {
  kind: WizardSurface;
  available: boolean;
  children: React.ReactNode;
}) {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const scope = useMemo<AiChatScope | null>(
    () => (eventId ? { kind: "event", id: eventId } : null),
    [eventId],
  );
  useEffect(() => {
    const store = useAiAvailabilityStore.getState();
    store.setDrawerOpen(false);
    store.setWizardSurface(kind);
    return () => useAiAvailabilityStore.getState().setWizardSurface(null);
  }, [kind]);
  return (
    <AiAssistantProvider
      scope={scope}
      available={available}
      chrome={false}
      ephemeralTranscript
      mountSupportTools={false}
    >
      {children}
    </AiAssistantProvider>
  );
}

/**
 * COMPANY-scoped creation-wizard mount — for the create-event wizard, the one
 * wizard that runs with no event open (/create-event under DashboardShell).
 * Same headless/ephemeral shape as {@link WizardScopedAiAssistant}, but the
 * scope comes from the companyId cookie and availability is gated to the
 * roles that may create events (OWNER/ADMIN — mirrors the server's
 * event-create + wizard-tool gates). NESTS inside CompanyScopedAiAssistant.
 */
export function CompanyWizardScopedAiAssistant({
  kind,
  children,
}: {
  kind: WizardSurface;
  children: React.ReactNode;
}) {
  const { progress } = useOnboarding();
  const [companyId, setCompanyId] = useState<string | null>(readCompanyIdSync);
  useEffect(() => {
    let active = true;
    const read = () => {
      getCookie("companyId").then((value) => {
        if (active && value && COMPANY_ID_SHAPE.test(value)) setCompanyId(value);
      });
    };
    read();
    window.addEventListener("veltric:auth-changed", read);
    return () => {
      active = false;
      window.removeEventListener("veltric:auth-changed", read);
    };
  }, []);
  const scope = useMemo<AiChatScope | null>(
    () => (companyId ? { kind: "company", id: companyId } : null),
    [companyId],
  );
  const liveRole = progress?.companyRole;
  useCompanyRoleCacheWriter(companyId, liveRole);
  const role = liveRole ?? (companyId ? companyRoleCache.get(companyId) : undefined);
  // OWNER/ADMIN only — the server injects the event-create wizard tools for
  // exactly these roles; an EDITOR would get a copilot that can't apply.
  const available = role === "OWNER" || role === "ADMIN";
  useEffect(() => {
    const store = useAiAvailabilityStore.getState();
    store.setDrawerOpen(false);
    store.setWizardSurface(kind);
    return () => useAiAvailabilityStore.getState().setWizardSurface(null);
  }, [kind]);
  return (
    <AiAssistantProvider
      scope={scope}
      available={available}
      chrome={false}
      ephemeralTranscript
      mountSupportTools={false}
    >
      {children}
    </AiAssistantProvider>
  );
}

/** Mirrors the server's @RolesAllowed on the company-chat endpoint. */
const COMPANY_AI_ROLES = new Set(["OWNER", "ADMIN", "EDITOR"]);

const COMPANY_ID_SHAPE = /^[a-f0-9]{24}$/;

/** Synchronous mirror of getCookie("companyId")'s primary (localStorage) path. */
function readCompanyIdSync(): string | null {
  const value = tokenStore.getCompanyId();
  return value && COMPANY_ID_SHAPE.test(value) ? value : null;
}

/**
 * Last server-confirmed company role per companyId. OnboardingProvider
 * refetches progress from the network on EVERY shell mount, so after an
 * event→dashboard swap `progress` is null for a full roundtrip — without
 * this cache `available` would stay false for that window, unmounting an
 * open drawer and popping it back in when the fetch lands. The cache only
 * bridges that loading window (live value wins the moment it arrives), and
 * the server's role gate stays authoritative for the actual chat calls.
 */
const companyRoleCache = new Map<string, string>();

/**
 * Both shells mount inside OnboardingProvider, so whichever shell has live
 * progress keeps the cache warm — an event page pre-warms it, making even
 * the FIRST event→dashboard swap seamless.
 */
function useCompanyRoleCacheWriter(companyId: string | null, liveRole: string | undefined) {
  useEffect(() => {
    if (companyId && liveRole) companyRoleCache.set(companyId, liveRole);
  }, [companyId, liveRole]);
}

/**
 * Dashboard mount (no event open): company scope from the companyId cookie,
 * availability from the company role (OnboardingProvider's progress — the
 * DashboardShell always mounts inside it). Mirrors the event side's
 * can("ai:use") gate so a company VIEWER never sees a button that 403s.
 */
export function CompanyScopedAiAssistant({ children }: { children: React.ReactNode }) {
  const { progress } = useOnboarding();
  // Seeded synchronously so the scope exists on the FIRST render after a
  // shell swap — the async getCookie read below would leave scope null for
  // one extra render (a frame where an open drawer has nothing to mount in).
  const [companyId, setCompanyId] = useState<string | null>(readCompanyIdSync);
  useEffect(() => {
    let active = true;
    const read = () => {
      getCookie("companyId").then((value) => {
        if (active && value && COMPANY_ID_SHAPE.test(value)) setCompanyId(value);
      });
    };
    read();
    // Re-read on auth changes (setUncriticalCookie emits this) so a login
    // bootstrap that lands after mount — or an in-SPA company change —
    // doesn't leave the assistant scopeless until a full reload.
    window.addEventListener("veltric:auth-changed", read);
    return () => {
      active = false;
      window.removeEventListener("veltric:auth-changed", read);
    };
  }, []);
  const scope = useMemo<AiChatScope | null>(
    () => (companyId ? { kind: "company", id: companyId } : null),
    [companyId],
  );
  const liveRole = progress?.companyRole;
  useCompanyRoleCacheWriter(companyId, liveRole);
  // progress is null while OnboardingProvider (re)fetches — fall back to the
  // cached verdict for THIS companyId so an open drawer survives the
  // roundtrip instead of unmounting and popping back in.
  const role = liveRole ?? (companyId ? companyRoleCache.get(companyId) : undefined);
  const available = COMPANY_AI_ROLES.has(role ?? "");
  return (
    <AiAssistantProvider scope={scope} available={available}>
      {children}
    </AiAssistantProvider>
  );
}
